/**
 * Client-side Hotel model with read-only access to hotel data
 */

import type { WidgetImage } from '@elara/domain/models/schemas/widget-schemas';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { MapPoint } from '@elara/client-core/ui-contracts/map-point';

import { HotelModelSpec } from '@elara/domain/models/HotelModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';
import { requestHotelPhotoEnrichment } from '#lib/enrich-hotel-photos.js';
import { parseHHMMToTime } from '#lib/itinerary-utils.js';
import { resolveMediaUrl } from '#lib/media-url.js';
import { formatAddress } from '#lib/utils.js';

/**
 * Hotel model for client-side usage
 * Provides read-only access to hotel data from Firestore
 */
export function createClientHotelModel(firestore: FirestoreContainer) {
  const model = createClientModel(firestore, HotelModelSpec, () => ({
    get checkInTime(): string | undefined {
      const rawTime = this.property?.checkIn || this.policies?.checkIn;
      return parseHHMMToTime(rawTime);
    },

    get checkOutTime(): string | undefined {
      const rawTime = this.property?.checkOut || this.policies?.checkOut;
      return parseHHMMToTime(rawTime);
    },

    get formattedAddress(): string {
      return formatAddress(this.address);
    },

    get previewImages(): WidgetImage[] {
      // Stored head images (the first ~10), written by the server photo pipeline
      // (ensureHotelPhotos). NOT the hotel's full photo set — the complete gallery lives in
      // hotels/{id}/media/gallery, loaded via useHotelGallery where full browsing matters.
      if (this.images && this.images.length > 0) {
        return this.images.map((image) => ({ ...image, url: resolveMediaUrl(image.url) }));
      }
      // Pre-pipeline hotel (never fetched): fire the server backfill once — the Firestore
      // listener delivers the images reactively when it lands. Covers every render surface on
      // web and mobile because they all go through this getter.
      if (!this.imagesMeta) {
        requestHotelPhotoEnrichment(this.id);
      }
      return [];
    },

    get imageUrl(): string | undefined {
      return this.previewImages?.[0]?.url;
    },

    get imageUrls(): string[] {
      return this.previewImages.map((image) => image.url).filter(Boolean) as string[];
    },

    get shortAddress(): string {
      return formatAddress({ line1: this.address?.line1, line2: this.address?.line2, city: this.address?.city });
    },

    get mapPoint(): MapPoint {
      const point: MapPoint = { id: this.id, label: this.name, address: this.formattedAddress };
      return point;
    },

    get mapUrl(): string | undefined {
      if (!this.name) return undefined;

      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.name)}`;
    },

    get isSerandipiansAffiliated(): boolean {
      return this.affiliations?.serandipians?.name === 'serandipians';
    },

    get serandipiansBenefits(): string[] | undefined {
      return this.affiliations?.serandipians?.benefits?.split('|');
    },
  }));
  return model;
}

export type ClientHotelModel = ReturnType<typeof createClientHotelModel>;
export type Hotel = ClientInstanceOf<ClientHotelModel>;
